// src/experiments/sessionExposures.js
//
// Which experiments this visitor has been exposed to in this session, and in
// which arm. Written by exposure.js when a tested surface renders. Read by
// the stamping in exposure.js, and by assignments.js to keep a visitor in
// the arm they have already seen while a soft remote change is in effect.
// Lives in sessionStorage: a browser session is the closest thing to "the
// life of a flow session" the app can observe.

import { findExperiment } from './registry';

const SESSION_KEY = 'hippo_exp_exposed';
let cache = null;

function load() {
  if (cache) return cache;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    cache = parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    cache = {};
  }
  return cache;
}

function save() {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(cache || {}));
  } catch {
    // storage blocked
  }
}

// Entries whose experiment no longer exists in the config are dropped on
// read, so a deleted experiment stops mattering without a cleanup step.
export function getSessionExposures() {
  const session = load();
  const live = {};
  for (const [key, variant] of Object.entries(session)) {
    if (findExperiment(key) && typeof variant === 'string') live[key] = variant;
  }
  return live;
}

// Returns true when something changed.
export function rememberExposure(key, variant) {
  const session = load();
  if (session[key] === variant) return false;
  session[key] = variant;
  save();
  return true;
}

export function forgetExposure(key) {
  const session = load();
  if (!(key in session)) return;
  delete session[key];
  save();
}

// Drops the in-memory copy so the next read comes from storage.
export function reloadSessionExposures() {
  cache = null;
}

export function resetSessionExposures() {
  cache = null;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

export const EXPOSURE_SESSION_KEY = SESSION_KEY;
